import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfMonth, subMonths, format, differenceInHours } from 'date-fns';
import { fetchTestOrganizationIds, fetchTestUserIds, fetchTestJobIds } from './useTestOrganizations';

// Types
export interface FunnelStage {
  name: string;
  count: number;
  conversionRate: number;
}

export interface BuyerFunnelData {
  stages: FunnelStage[];
  isLoading: boolean;
}

export interface VendorFunnelData {
  stages: FunnelStage[];
  isLoading: boolean;
}

export interface LiquidityMetrics {
  avgQuotesPerRfq: number;
  rfqsWith3PlusQuotes: number;
  rfqsWith3PlusQuotesPercent: number;
  avgTimeToFirstQuoteHours: number;
  rfqsWithAward: number;
  rfqsWithAwardPercent: number;
  totalRfqs: number;
  isLoading: boolean;
}

export interface VendorLeaderboardEntry {
  vendorId: string;
  vendorName: string;
  companyName: string;
  quotesSubmitted: number;
  quotesWon: number;
  winRate: number;
  avgResponseTimeHours: number;
  totalGmvCents: number;
  avgRating: number | null;
}

export interface RetentionCohort {
  cohortMonth: string;
  signupCount: number;
  retentionByMonth: { month: number; count: number; percent: number }[];
}

export interface DateRangeFilter {
  startDate: Date;
  endDate: Date;
}

export interface AnalyticsOptions {
  includeTestData?: boolean;
}

// Buyer Activation Funnel Hook
export function useBuyerActivationFunnel(dateRange?: DateRangeFilter, options: AnalyticsOptions = {}) {
  const { includeTestData = false } = options;
  
  return useQuery({
    queryKey: ['marketplace-analytics', 'buyer-funnel', dateRange?.startDate?.toISOString(), dateRange?.endDate?.toISOString(), includeTestData],
    queryFn: async () => {
      const startDate = dateRange?.startDate?.toISOString();
      const endDate = dateRange?.endDate?.toISOString();

      // Get test org/user IDs if excluding test data
      let testOrgIds: string[] = [];
      let testUserIds: string[] = [];
      if (!includeTestData) {
        testOrgIds = await fetchTestOrganizationIds();
        testUserIds = await fetchTestUserIds(testOrgIds);
      }

      // Stage 1: All client signups
      let signupsQuery = supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('role', 'client');
      if (startDate) signupsQuery = signupsQuery.gte('created_at', startDate);
      if (endDate) signupsQuery = signupsQuery.lte('created_at', endDate);
      if (testOrgIds.length > 0) {
        signupsQuery = signupsQuery.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
      }
      const { count: signups } = await signupsQuery;

      // Stage 2: Clients who updated their profile
      let profileUpdatesQuery = supabase
        .from('analytics_events')
        .select('user_id')
        .eq('event_name', 'profile_updated')
        .eq('user_role', 'client');
      if (startDate) profileUpdatesQuery = profileUpdatesQuery.gte('created_at', startDate);
      if (endDate) profileUpdatesQuery = profileUpdatesQuery.lte('created_at', endDate);
      if (testUserIds.length > 0) {
        profileUpdatesQuery = profileUpdatesQuery.not('user_id', 'in', `(${testUserIds.join(',')})`);
      }
      const { data: profileUpdates } = await profileUpdatesQuery;
      const profileUpdatedCount = new Set(profileUpdates?.map(e => e.user_id) || []).size;

      // Stage 3: Organizations that created a job
      let jobOrgsQuery = supabase
        .from('jobs')
        .select('organization_id');
      if (startDate) jobOrgsQuery = jobOrgsQuery.gte('created_at', startDate);
      if (endDate) jobOrgsQuery = jobOrgsQuery.lte('created_at', endDate);
      if (testOrgIds.length > 0) {
        jobOrgsQuery = jobOrgsQuery.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
      }
      const { data: jobOrgs } = await jobOrgsQuery;
      const createdJobCount = new Set(jobOrgs?.map(j => j.organization_id) || []).size;

      // Stage 4: Jobs that have an RFQ published
      let rfqJobsQuery = supabase
        .from('rfqs')
        .select('job_id, jobs!inner(organization_id)')
        .in('status', ['open', 'closed', 'awarded']);
      if (startDate) rfqJobsQuery = rfqJobsQuery.gte('created_at', startDate);
      if (endDate) rfqJobsQuery = rfqJobsQuery.lte('created_at', endDate);
      const { data: rfqJobs } = await rfqJobsQuery;
      
      let filteredRfqJobs = rfqJobs || [];
      if (testOrgIds.length > 0) {
        filteredRfqJobs = filteredRfqJobs.filter((r: any) => !testOrgIds.includes(r.jobs?.organization_id));
      }
      const publishedRfqCount = new Set(filteredRfqJobs.map((r: any) => r.job_id) || []).size;

      // Stage 5: RFQs that received at least one quote
      let quotedRfqsQuery = supabase
        .from('bids')
        .select('rfq_id, rfqs!inner(job_id, jobs!inner(organization_id))');
      if (startDate) quotedRfqsQuery = quotedRfqsQuery.gte('created_at', startDate);
      if (endDate) quotedRfqsQuery = quotedRfqsQuery.lte('created_at', endDate);
      const { data: quotedRfqs } = await quotedRfqsQuery;
      
      let filteredQuotedRfqs = quotedRfqs || [];
      if (testOrgIds.length > 0) {
        filteredQuotedRfqs = filteredQuotedRfqs.filter((b: any) => !testOrgIds.includes(b.rfqs?.jobs?.organization_id));
      }
      const gotQuotesCount = new Set(filteredQuotedRfqs.map((b: any) => b.rfq_id) || []).size;

      // Stage 6: Jobs that have been awarded
      let awardedQuery = supabase
        .from('awards')
        .select('id, jobs!inner(organization_id)', { count: 'exact' });
      if (startDate) awardedQuery = awardedQuery.gte('awarded_at', startDate);
      if (endDate) awardedQuery = awardedQuery.lte('awarded_at', endDate);
      const { data: awardedData } = await awardedQuery;
      
      let filteredAwarded = awardedData || [];
      if (testOrgIds.length > 0) {
        filteredAwarded = filteredAwarded.filter((a: any) => !testOrgIds.includes(a.jobs?.organization_id));
      }
      const awardedCount = filteredAwarded.length;

      // Stage 7: Jobs that are completed
      let completedQuery = supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'completed');
      if (startDate) completedQuery = completedQuery.gte('updated_at', startDate);
      if (endDate) completedQuery = completedQuery.lte('updated_at', endDate);
      if (testOrgIds.length > 0) {
        completedQuery = completedQuery.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
      }
      const { count: completedCount } = await completedQuery;

      // Stage 8: Organizations with 2+ jobs (repeat buyers)
      let repeatBuyersQuery = supabase
        .from('jobs')
        .select('organization_id');
      if (startDate) repeatBuyersQuery = repeatBuyersQuery.gte('created_at', startDate);
      if (endDate) repeatBuyersQuery = repeatBuyersQuery.lte('created_at', endDate);
      if (testOrgIds.length > 0) {
        repeatBuyersQuery = repeatBuyersQuery.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
      }
      const { data: repeatBuyers } = await repeatBuyersQuery;
      const orgJobCounts = (repeatBuyers || []).reduce((acc, job) => {
        acc[job.organization_id] = (acc[job.organization_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      const repeatBuyerCount = Object.values(orgJobCounts).filter(c => c >= 2).length;

      const stages = [
        { name: 'Signed Up', count: signups || 0 },
        { name: 'Updated Profile', count: profileUpdatedCount },
        { name: 'Created Job', count: createdJobCount },
        { name: 'Published RFQ', count: publishedRfqCount },
        { name: 'Got Quotes', count: gotQuotesCount },
        { name: 'Awarded Job', count: awardedCount || 0 },
        { name: 'Job Completed', count: completedCount || 0 },
        { name: 'Repeat Buyer', count: repeatBuyerCount },
      ];

      // Calculate conversion rates
      return stages.map((stage, i) => ({
        ...stage,
        conversionRate: i === 0 ? 100 : stages[i - 1].count > 0 
          ? Math.round((stage.count / stages[i - 1].count) * 100) 
          : 0,
      }));
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Vendor Activation Funnel Hook
export function useVendorActivationFunnel(dateRange?: DateRangeFilter, options: AnalyticsOptions = {}) {
  const { includeTestData = false } = options;
  
  return useQuery({
    queryKey: ['marketplace-analytics', 'vendor-funnel', dateRange?.startDate?.toISOString(), dateRange?.endDate?.toISOString(), includeTestData],
    queryFn: async () => {
      const startDate = dateRange?.startDate?.toISOString();
      const endDate = dateRange?.endDate?.toISOString();

      // Get test org/user IDs if excluding test data
      let testOrgIds: string[] = [];
      let testUserIds: string[] = [];
      let testJobIds: string[] = [];
      if (!includeTestData) {
        testOrgIds = await fetchTestOrganizationIds();
        testUserIds = await fetchTestUserIds(testOrgIds);
        testJobIds = await fetchTestJobIds(testOrgIds);
      }

      // Stage 1: All vendor signups
      let signupsQuery = supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('role', 'vendor');
      if (startDate) signupsQuery = signupsQuery.gte('created_at', startDate);
      if (endDate) signupsQuery = signupsQuery.lte('created_at', endDate);
      if (testOrgIds.length > 0) {
        signupsQuery = signupsQuery.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
      }
      const { count: signups } = await signupsQuery;

      // Stage 2: Vendors who updated their profile
      let profileUpdatesQuery = supabase
        .from('analytics_events')
        .select('user_id')
        .eq('event_name', 'profile_updated')
        .eq('user_role', 'vendor');
      if (startDate) profileUpdatesQuery = profileUpdatesQuery.gte('created_at', startDate);
      if (endDate) profileUpdatesQuery = profileUpdatesQuery.lte('created_at', endDate);
      if (testUserIds.length > 0) {
        profileUpdatesQuery = profileUpdatesQuery.not('user_id', 'in', `(${testUserIds.join(',')})`);
      }
      const { data: profileUpdates } = await profileUpdatesQuery;
      const profileUpdatedCount = new Set(profileUpdates?.map(e => e.user_id) || []).size;

      // Stage 3: Vendors who submitted for verification (query actual verification_requests table)
      let verificationQuery = supabase
        .from('verification_requests')
        .select('vendor_profile_id');
      if (startDate) verificationQuery = verificationQuery.gte('requested_at', startDate);
      if (endDate) verificationQuery = verificationQuery.lte('requested_at', endDate);
      const { data: verificationRequests } = await verificationQuery;

      // Get unique vendor profile IDs who submitted verification
      let verificationVendorIds = new Set(verificationRequests?.map(vr => vr.vendor_profile_id) || []);

      // Filter out test organizations if needed
      if (testOrgIds.length > 0 && verificationVendorIds.size > 0) {
        const { data: testVendorProfiles } = await supabase
          .from('vendor_profiles_enhanced')
          .select('id')
          .in('organization_id', testOrgIds);
        const testVendorProfileIds = new Set(testVendorProfiles?.map(v => v.id) || []);
        verificationVendorIds = new Set(
          [...verificationVendorIds].filter(id => !testVendorProfileIds.has(id))
        );
      }
      const verificationCount = verificationVendorIds.size;

      // Stage 4: Vendors who submitted their first quote (excluding bids on test org jobs)
      let vendorBidsQuery = supabase
        .from('bids')
        .select('vendor_user_id');
      if (startDate) vendorBidsQuery = vendorBidsQuery.gte('created_at', startDate);
      if (endDate) vendorBidsQuery = vendorBidsQuery.lte('created_at', endDate);
      if (testUserIds.length > 0) {
        vendorBidsQuery = vendorBidsQuery.not('vendor_user_id', 'in', `(${testUserIds.join(',')})`);
      }
      const { data: vendorBids } = await vendorBidsQuery;
      const submittedQuoteCount = new Set(vendorBids?.map(b => b.vendor_user_id) || []).size;

      // Stage 5: Vendors who won their first project
      let winningBidsQuery = supabase
        .from('awards')
        .select('winning_bid_id, jobs!inner(organization_id)')
        .not('winning_bid_id', 'is', null);
      if (startDate) winningBidsQuery = winningBidsQuery.gte('awarded_at', startDate);
      if (endDate) winningBidsQuery = winningBidsQuery.lte('awarded_at', endDate);
      const { data: winningBids } = await winningBidsQuery;
      
      let filteredWinningBids = winningBids || [];
      if (testOrgIds.length > 0) {
        filteredWinningBids = filteredWinningBids.filter((a: any) => !testOrgIds.includes(a.jobs?.organization_id));
      }
      
      // Get vendor IDs from winning bids
      const winningBidIds = filteredWinningBids.map(a => a.winning_bid_id).filter(Boolean) || [];
      let wonProjectVendors = new Set<string>();
      
      if (winningBidIds.length > 0) {
        const { data: winningBidDetails } = await supabase
          .from('bids')
          .select('vendor_user_id')
          .in('id', winningBidIds as string[]);
        wonProjectVendors = new Set(winningBidDetails?.map(b => b.vendor_user_id) || []);
      }
      const wonFirstCount = wonProjectVendors.size;

      // Stage 6: Vendors who won multiple projects  
      const vendorWinCounts: Record<string, number> = {};
      if (winningBidIds.length > 0) {
        const { data: allWinningBids } = await supabase
          .from('bids')
          .select('vendor_user_id')
          .in('id', winningBidIds as string[]);
        allWinningBids?.forEach(bid => {
          vendorWinCounts[bid.vendor_user_id] = (vendorWinCounts[bid.vendor_user_id] || 0) + 1;
        });
      }
      const multipleWinsCount = Object.values(vendorWinCounts).filter(c => c >= 2).length;

      const stages = [
        { name: 'Signed Up', count: signups || 0 },
        { name: 'Updated Profile', count: profileUpdatedCount },
        { name: 'Submitted Verification', count: verificationCount || 0 },
        { name: 'First Quote', count: submittedQuoteCount },
        { name: 'First Win', count: wonFirstCount },
        { name: 'Multiple Wins', count: multipleWinsCount },
      ];

      return stages.map((stage, i) => ({
        ...stage,
        conversionRate: i === 0 ? 100 : stages[i - 1].count > 0 
          ? Math.round((stage.count / stages[i - 1].count) * 100) 
          : 0,
      }));
    },
    staleTime: 5 * 60 * 1000,
  });
}

// Marketplace Liquidity Hook
export function useMarketplaceLiquidity(dateRange?: DateRangeFilter, options: AnalyticsOptions = {}) {
  const { includeTestData = false } = options;
  
  return useQuery({
    queryKey: ['marketplace-analytics', 'liquidity', dateRange?.startDate?.toISOString(), dateRange?.endDate?.toISOString(), includeTestData],
    queryFn: async () => {
      const startDate = dateRange?.startDate?.toISOString();
      const endDate = dateRange?.endDate?.toISOString();

      // Get test org IDs if excluding test data
      let testOrgIds: string[] = [];
      if (!includeTestData) {
        testOrgIds = await fetchTestOrganizationIds();
      }

      // Get all RFQs with job org info for filtering
      let rfqsQuery = supabase
        .from('rfqs')
        .select('id, opens_at, job_id, jobs!inner(organization_id)', { count: 'exact' });
      if (startDate) rfqsQuery = rfqsQuery.gte('created_at', startDate);
      if (endDate) rfqsQuery = rfqsQuery.lte('created_at', endDate);
      const { data: rfqsData } = await rfqsQuery;

      // Filter out test org RFQs
      let rfqs = rfqsData || [];
      if (testOrgIds.length > 0) {
        rfqs = rfqs.filter((r: any) => !testOrgIds.includes(r.jobs?.organization_id));
      }
      const totalRfqs = rfqs.length;
      const rfqIds = rfqs.map(r => r.id);

      // Get all bids for non-test RFQs
      let bids: any[] = [];
      if (rfqIds.length > 0) {
        let bidsQuery = supabase
          .from('bids')
          .select('id, rfq_id, created_at')
          .in('rfq_id', rfqIds);
        if (startDate) bidsQuery = bidsQuery.gte('created_at', startDate);
        if (endDate) bidsQuery = bidsQuery.lte('created_at', endDate);
        const { data: bidsData } = await bidsQuery;
        bids = bidsData || [];
      }

      // Count bids per RFQ
      const bidsByRfq: Record<string, { count: number; firstBidAt: string | null }> = {};
      bids.forEach(bid => {
        if (!bidsByRfq[bid.rfq_id]) {
          bidsByRfq[bid.rfq_id] = { count: 0, firstBidAt: null };
        }
        bidsByRfq[bid.rfq_id].count++;
        if (!bidsByRfq[bid.rfq_id].firstBidAt || bid.created_at < bidsByRfq[bid.rfq_id].firstBidAt!) {
          bidsByRfq[bid.rfq_id].firstBidAt = bid.created_at;
        }
      });

      const rfqsWithBids = Object.keys(bidsByRfq).length;
      const totalBids = bids.length;
      const avgQuotesPerRfq = rfqsWithBids > 0 ? totalBids / rfqsWithBids : 0;

      // RFQs with 3+ quotes
      const rfqsWith3Plus = Object.values(bidsByRfq).filter(r => r.count >= 3).length;
      const rfqsWith3PlusPercent = totalRfqs > 0 
        ? (rfqsWith3Plus / totalRfqs) * 100 
        : 0;

      // Average time to first quote
      let totalHoursToFirstQuote = 0;
      let rfqsWithFirstQuote = 0;
      rfqs.forEach((rfq: any) => {
        const bidInfo = bidsByRfq[rfq.id];
        if (bidInfo?.firstBidAt && rfq.opens_at) {
          const hours = differenceInHours(new Date(bidInfo.firstBidAt), new Date(rfq.opens_at));
          if (hours >= 0) {
            totalHoursToFirstQuote += hours;
            rfqsWithFirstQuote++;
          }
        }
      });
      const avgTimeToFirstQuoteHours = rfqsWithFirstQuote > 0 
        ? totalHoursToFirstQuote / rfqsWithFirstQuote 
        : 0;

      // RFQs with awards
      let awardsQuery = supabase
        .from('awards')
        .select('job_id, jobs!inner(organization_id)');
      if (startDate) awardsQuery = awardsQuery.gte('awarded_at', startDate);
      if (endDate) awardsQuery = awardsQuery.lte('awarded_at', endDate);
      const { data: awardsData } = await awardsQuery;
      
      let filteredAwards = awardsData || [];
      if (testOrgIds.length > 0) {
        filteredAwards = filteredAwards.filter((a: any) => !testOrgIds.includes(a.jobs?.organization_id));
      }
      const rfqsWithAward = filteredAwards.length;

      const rfqsWithAwardPercent = totalRfqs > 0 
        ? (rfqsWithAward / totalRfqs) * 100 
        : 0;

      return {
        avgQuotesPerRfq: Math.round(avgQuotesPerRfq * 10) / 10,
        rfqsWith3PlusQuotes: rfqsWith3Plus,
        rfqsWith3PlusQuotesPercent: Math.round(rfqsWith3PlusPercent),
        avgTimeToFirstQuoteHours: Math.round(avgTimeToFirstQuoteHours),
        rfqsWithAward,
        rfqsWithAwardPercent: Math.round(rfqsWithAwardPercent),
        totalRfqs,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

// Vendor Leaderboard Hook
export function useVendorLeaderboard(dateRange?: DateRangeFilter, options: AnalyticsOptions = {}) {
  const { includeTestData = false } = options;
  
  return useQuery({
    queryKey: ['marketplace-analytics', 'vendor-leaderboard', dateRange?.startDate?.toISOString(), dateRange?.endDate?.toISOString(), includeTestData],
    queryFn: async () => {
      const startDate = dateRange?.startDate?.toISOString();
      const endDate = dateRange?.endDate?.toISOString();

      // Get test org IDs if excluding test data
      let testOrgIds: string[] = [];
      let testUserIds: string[] = [];
      if (!includeTestData) {
        testOrgIds = await fetchTestOrganizationIds();
        testUserIds = await fetchTestUserIds(testOrgIds);
      }

      // Get all bids with RFQ info to filter by job org
      let bidsQuery = supabase
        .from('bids')
        .select('id, vendor_user_id, rfq_id, created_at, rfqs!inner(id, opens_at, jobs!inner(organization_id))');
      if (startDate) bidsQuery = bidsQuery.gte('created_at', startDate);
      if (endDate) bidsQuery = bidsQuery.lte('created_at', endDate);
      const { data: bidsData } = await bidsQuery;

      // Filter out bids from test user vendors or on test org jobs
      let bids = bidsData || [];
      if (testOrgIds.length > 0 || testUserIds.length > 0) {
        bids = bids.filter((b: any) => {
          const isTestVendor = testUserIds.includes(b.vendor_user_id);
          const isTestJob = testOrgIds.includes(b.rfqs?.jobs?.organization_id);
          return !isTestVendor && !isTestJob;
        });
      }

      // Get all awards with job org info
      let awardsQuery = supabase
        .from('awards')
        .select('id, winning_bid_id, total_award_amount_cents, jobs!inner(organization_id)')
        .not('winning_bid_id', 'is', null);
      if (startDate) awardsQuery = awardsQuery.gte('awarded_at', startDate);
      if (endDate) awardsQuery = awardsQuery.lte('awarded_at', endDate);
      const { data: awardsData } = await awardsQuery;
      
      let awards = awardsData || [];
      if (testOrgIds.length > 0) {
        awards = awards.filter((a: any) => !testOrgIds.includes(a.jobs?.organization_id));
      }
      
      // Get winning bid vendor IDs
      const winningBidIds = awards.map(a => a.winning_bid_id).filter(Boolean) as string[];
      const winningBidsResult = winningBidIds.length > 0 
        ? await supabase.from('bids').select('id, vendor_user_id').in('id', winningBidIds)
        : { data: [] as { id: string; vendor_user_id: string }[] };
      const winningBidVendorMap = new Map<string, string>(
        (winningBidsResult.data || []).map(b => [b.id, b.vendor_user_id])
      );

      // Get vendor profiles (exclude test org vendors)
      let vendorProfilesQuery = supabase
        .from('vendor_profiles_enhanced')
        .select('user_id, business_name, first_name, last_name');
      if (testOrgIds.length > 0) {
        vendorProfilesQuery = vendorProfilesQuery.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
      }
      const { data: vendorProfiles } = await vendorProfilesQuery;

      // Get reviews for vendors
      const { data: reviews } = await supabase
        .from('reviews')
        .select('reviewee_user_id, overall_rating');

      // Build vendor stats
      const vendorStats: Record<string, {
        quotesSubmitted: number;
        quotesWon: number;
        totalGmvCents: number;
        responseTimes: number[];
        ratings: number[];
      }> = {};

      // Process bids
      bids.forEach((bid: any) => {
        const vendorId = bid.vendor_user_id;
        if (!vendorStats[vendorId]) {
          vendorStats[vendorId] = {
            quotesSubmitted: 0,
            quotesWon: 0,
            totalGmvCents: 0,
            responseTimes: [],
            ratings: [],
          };
        }
        vendorStats[vendorId].quotesSubmitted++;

        // Calculate response time
        const opensAt = bid.rfqs?.opens_at;
        if (opensAt) {
          const hours = differenceInHours(new Date(bid.created_at), new Date(opensAt));
          if (hours >= 0) {
            vendorStats[vendorId].responseTimes.push(hours);
          }
        }
      });

      // Process awards
      awards.forEach(award => {
        const vendorId = winningBidVendorMap.get(award.winning_bid_id!);
        if (vendorId && vendorStats[vendorId]) {
          vendorStats[vendorId].quotesWon++;
          vendorStats[vendorId].totalGmvCents += award.total_award_amount_cents || 0;
        }
      });

      // Process reviews
      (reviews || []).forEach(review => {
        if (vendorStats[review.reviewee_user_id]) {
          vendorStats[review.reviewee_user_id].ratings.push(review.overall_rating);
        }
      });

      // Build leaderboard
      const profileMap = new Map(
        (vendorProfiles || []).map(p => [p.user_id, p])
      );

      const leaderboard: VendorLeaderboardEntry[] = Object.entries(vendorStats)
        .map(([vendorId, stats]) => {
          const profile = profileMap.get(vendorId);
          const avgResponseTime = stats.responseTimes.length > 0
            ? stats.responseTimes.reduce((a, b) => a + b, 0) / stats.responseTimes.length
            : 0;
          const avgRating = stats.ratings.length > 0
            ? stats.ratings.reduce((a, b) => a + b, 0) / stats.ratings.length
            : null;

          return {
            vendorId,
            vendorName: profile 
              ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown'
              : 'Unknown',
            companyName: profile?.business_name || 'Unknown',
            quotesSubmitted: stats.quotesSubmitted,
            quotesWon: stats.quotesWon,
            winRate: stats.quotesSubmitted > 0 
              ? Math.round((stats.quotesWon / stats.quotesSubmitted) * 100) 
              : 0,
            avgResponseTimeHours: Math.round(avgResponseTime),
            totalGmvCents: stats.totalGmvCents,
            avgRating: avgRating ? Math.round(avgRating * 10) / 10 : null,
          };
        })
        .sort((a, b) => b.totalGmvCents - a.totalGmvCents)
        .slice(0, 20);

      return leaderboard;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// Retention Cohort Hook
export function useRetentionCohorts(userType: 'client' | 'vendor', options: AnalyticsOptions = {}) {
  const { includeTestData = false } = options;
  
  return useQuery({
    queryKey: ['marketplace-analytics', 'retention', userType, includeTestData],
    queryFn: async () => {
      const now = new Date();
      const cohorts: RetentionCohort[] = [];

      // Get test org/user IDs if excluding test data
      let testOrgIds: string[] = [];
      let testUserIds: string[] = [];
      if (!includeTestData) {
        testOrgIds = await fetchTestOrganizationIds();
        testUserIds = await fetchTestUserIds(testOrgIds);
      }

      // Get last 6 months of cohorts
      for (let i = 5; i >= 0; i--) {
        const cohortStart = startOfMonth(subMonths(now, i));
        const cohortEnd = startOfMonth(subMonths(now, i - 1));
        const cohortMonth = format(cohortStart, 'yyyy-MM');

        // Get signups in this cohort
        let signupsQuery = supabase
          .from('profiles')
          .select('user_id, created_at')
          .eq('role', userType)
          .gte('created_at', cohortStart.toISOString())
          .lt('created_at', cohortEnd.toISOString());
        if (testOrgIds.length > 0) {
          signupsQuery = signupsQuery.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
        }
        const { data: signups } = await signupsQuery;

        const signupCount = signups?.length || 0;
        const signupUserIds = signups?.map(s => s.user_id) || [];

        if (signupCount === 0) {
          cohorts.push({
            cohortMonth,
            signupCount: 0,
            retentionByMonth: [],
          });
          continue;
        }

        // Calculate retention for each subsequent month
        const retentionByMonth: { month: number; count: number; percent: number }[] = [];

        for (let m = 1; m <= Math.min(6, i + 1); m++) {
          const periodStart = startOfMonth(subMonths(now, i - m));
          const periodEnd = startOfMonth(subMonths(now, i - m - 1));

          // Check for any activity in this period
          const { data: activeUsers } = await supabase
            .from('analytics_events')
            .select('user_id')
            .in('user_id', signupUserIds)
            .gte('created_at', periodStart.toISOString())
            .lt('created_at', periodEnd.toISOString());

          const activeCount = new Set(activeUsers?.map(e => e.user_id) || []).size;
          retentionByMonth.push({
            month: m,
            count: activeCount,
            percent: Math.round((activeCount / signupCount) * 100),
          });
        }

        cohorts.push({
          cohortMonth,
          signupCount,
          retentionByMonth,
        });
      }

      return cohorts;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - retention is expensive to compute
  });
}
